import { Box, Typography, Stack, Alert } from "@mui/material";
import { useState } from "react";
import { Button, Spinner } from "react-bootstrap";

import {
  useAddNoteMutation,
  useUpdateNoteMutation,
} from "api/memberPortal/notes/notes.api";
import { useAppForm } from "components/Forms/AppForm";
import DialogModal from "components/Forms/components/modals/DialogModal";
import { useAuth } from "hooks/useAuth";

import {
  TQNoteFormProps,
  LabValidatorSmeNoteFormValues,
  LabValidatorSmeNoteFormSchema,
} from "./NoteForm.schema";

const TQNoteForm = ({ show, handleClose, note, tq }: TQNoteFormProps) => {
  const { currentUser, permissions } = useAuth();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [triggerUpdateNote] = useUpdateNoteMutation();
  const [triggerAddNote] = useAddNoteMutation();

  const isLab = permissions.role_type === "Lab";
  const isValidator = permissions.role_type === "Validator";
  const isSme = permissions.role_type === "SME";

  const tqNoteForm = useAppForm({
    defaultValues: {
      visible_to_labs: note?.visible_to_labs ?? false,
      visible_to_validators: note?.visible_to_validators ?? true,
      visible_to_smes: note?.visible_to_smes ?? true,
      title: note?.title,
      note: note?.note,
    } as LabValidatorSmeNoteFormValues,
    validators: {
      onSubmit: LabValidatorSmeNoteFormSchema,
    },
    onSubmit: async ({ value }) => {
      setError(null);
      setLoading(true);
      try {
        if (note?.note_id) {
          await triggerUpdateNote({
            noteId: note.note_id,
            body: {
              ...value,
            },
          });
        } else {
          await triggerAddNote({
            ...value,
            note_type: "technical_query",
            note_type_id: tq.tq_id,
            internal_only: false,
          }).unwrap();
        }
        handleClose();
        setLoading(false);
      } catch (error) {
        console.error(error);
        setError("Error adding note. Please try again.");
        setLoading(false);
      }
    },
  });

  return (
    <DialogModal
      title={`+ NOTE for TQ ${tq?.tq_id}`}
      open={show}
      maxWidth="md"
      fullWidth
      slotProps={{
        backdrop: {
          timeout: 300,
        },
      }}
    >
      <Box>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            tqNoteForm.handleSubmit();
          }}
        >
          {/* WARN: Custom styling from pre standardization! */}
          {/* TODO: Standardize when you come across this!  */}
          <Typography id="modal-modal-description" variant="body1">
            <Typography variant="body1" fontWeight="bold" className="mb-3">
              Submitter: {currentUser.username}
            </Typography>
            <Box className="mb-3">
              <Stack spacing={1}>
                {!isLab && (
                  <tqNoteForm.AppField name="visible_to_labs">
                    {(field) => (
                      <field.YesNoRadioButton
                        label="Visible To Labs?"
                        isHorizontal={true}
                      />
                    )}
                  </tqNoteForm.AppField>
                )}
                {!isValidator && (
                  <tqNoteForm.AppField name="visible_to_validators">
                    {(field) => (
                      <field.YesNoRadioButton
                        label="Visible To Validators?"
                        isHorizontal={true}
                      />
                    )}
                  </tqNoteForm.AppField>
                )}
                {!isSme && (
                  <tqNoteForm.AppField name="visible_to_smes">
                    {(field) => (
                      <field.YesNoRadioButton
                        label="Visible To SME's?"
                        isHorizontal={true}
                      />
                    )}
                  </tqNoteForm.AppField>
                )}
              </Stack>
            </Box>
            <Stack spacing={3}>
              <tqNoteForm.AppField name="title">
                {(field) => <field.TextInput label="Title" />}
              </tqNoteForm.AppField>

              <tqNoteForm.AppField name="note">
                {(field) => <field.HtmlEditor label="Overview" />}
              </tqNoteForm.AppField>
            </Stack>
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              p: 2,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Button variant="secondary" onClick={() => handleClose()}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={loading}
              type={loading ? "button" : "submit"}
              data-cy="submit-note"
            >
              {loading && <Spinner animation="border" size="sm" />}
              {note?.note_id ? "Update" : "Add"}
            </Button>
          </Box>
          {error && (
            <Alert severity="error" sx={{ mb: 2, width: "100%" }}>
              {error}
            </Alert>
          )}
        </form>
      </Box>
    </DialogModal>
  );
};

export default TQNoteForm;
